import React, { useState, useEffect } from 'react';
import PdfEditor from '../PdfEditor';
import { getChangeVers, changeFileName, regenChange, parseChange, toChangeInput, hasChange, emptySwap, CHANGE_KEY } from './changedoc';
import type { ChangeData, ChangeSwap } from './changedoc';
import { openS3 } from './docgen';
import { uploadDoc, fetchFileBlob } from './api';
import { getTxt, mutStore, UpLabel } from './stepcommon';
import type { Project, DocVerMap, DocVer } from './types';
import { saveS3ToProject } from './docgen';

interface Props {
  p: Project;
  formOpen: boolean;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
}
type RepKind = 'edit' | 'seal' | 'pdfonly';
const box: React.CSSProperties = { background: '#F8FAFC', border: '1px solid #E4E7EF', borderRadius: 8, padding: 10, marginBottom: 8 };
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' };

export default function ChangeDoc({ p, formOpen, onPatch, toast }: Props) {
  const [busy, setBusy] = useState('');
  const [more, setMore] = useState(false);
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [data, setData] = useState<ChangeData>(() => parseChange(getTxt(p, 's7', '_lpchange')));
  useEffect(() => { setData(parseChange(getTxt(p, 's7', '_lpchange'))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [p.id]);

  const vers = getChangeVers(p);
  const cur = vers[vers.length - 1];
  const real = !!(cur.s3doc || cur.s3pdf);

  const saveData = (d: ChangeData) => {
    setData(d);
    onPatch(mutStore(p, 's7', (s) => { s.txt = s.txt || {}; s.txt._lpchange = JSON.stringify(d); }));
  };
  const setSwap = (i: number, patch: Partial<ChangeSwap>) => saveData({ ...data, swaps: data.swaps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const addSwap = () => saveData({ ...data, swaps: [...data.swaps, emptySwap()] });
  const delSwap = (i: number) => saveData({ ...data, swaps: data.swaps.length > 1 ? data.swaps.filter((_, idx) => idx !== i) : [emptySwap()] });

  const gen = async () => {
    if (busy) return;
    if (!hasChange(data)) { toast('변경 내용을 입력하세요 (탈퇴자 또는 신규자)'); return; }
    setBusy('gen');
    try { await regenChange(p, toChangeInput(data), onPatch, toast); } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const dl = async (fmt: 'doc' | 'pdf') => {
    if (busy) return; setBusy(fmt);
    try {
      const key = fmt === 'doc' ? cur.s3doc : cur.s3pdf;
      if (!key) { toast('먼저 생성하세요'); return; }
      await saveS3ToProject(p, key, changeFileName(p, fmt === 'doc' ? 'docx' : 'pdf'), toast);   // 조합 저장 폴더로(2026-11-01)
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const pv = async () => {
    if (busy) return; setBusy('pv');
    try { if (!cur.s3pdf) { toast('먼저 생성하세요'); return; } await openS3(cur.s3pdf, changeFileName(p, 'pdf'), true); } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const replace = async (f: File, kind: RepKind) => {
    setBusy('up');
    try {
      const r = await uploadDoc(p.id, CHANGE_KEY, kind, f);
      const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
      if (!dv[CHANGE_KEY] || !dv[CHANGE_KEY].length || (dv[CHANGE_KEY].length === 1 && dv[CHANGE_KEY][0].kind === 'auto' && !dv[CHANGE_KEY][0].s3doc)) dv[CHANGE_KEY] = [{ ...cur }];
      const prev = dv[CHANGE_KEY][dv[CHANGE_KEY].length - 1];
      const n = dv[CHANGE_KEY].length + 1;
      const at = new Date().toISOString();
      const ver: DocVer = kind === 'edit'
        ? { label: '수정본 v' + n, doc: r.doc, pdf: r.pdf, kind: 'edit', s3doc: r.s3doc, s3pdf: r.s3pdf, at }
        : kind === 'seal'
          ? { label: '날인본 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'seal', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at }
          : { label: 'PDF만 교체 v' + n, doc: prev.doc, pdf: r.pdf, kind: 'pdfonly', s3doc: prev.s3doc || null, s3pdf: r.s3pdf, at };
      dv[CHANGE_KEY] = [...dv[CHANGE_KEY], ver];
      onPatch({ docver: dv });
      toast(kind === 'edit' ? '수정본 교체됨 · PDF 재변환' : kind === 'seal' ? '날인본 교체됨' : 'PDF만 교체됨');
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const openSign = async () => {
    if (busy) return;
    if (!cur.s3pdf) { toast('먼저 변경내역을 생성하세요'); return; }
    setBusy('sign');
    try {
      try { localStorage.removeItem('pdf_edit_url'); } catch (e) { /* noop */ }
      const blob = await fetchFileBlob(cur.s3pdf, changeFileName(p, 'pdf'));
      setEditUrl(URL.createObjectURL(blob));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  const closeEditor = () => { if (editUrl) URL.revokeObjectURL(editUrl); setEditUrl(null); };

  return (
    <div>
      {formOpen ? (
        <div style={box}>
          <div style={row}>
            <span style={{ fontSize: 12, color: '#475569', minWidth: 56 }}>변경일자</span>
            <input className="cotext" type="date" value={data.date} onChange={(e) => saveData({ ...data, date: e.target.value })} style={{ maxWidth: 170 }} />
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 6 }}>탈퇴자 → 신규자 (좌수 입력) · 한쪽만 입력해도 됩니다</div>
          {data.swaps.map((s, i) => (
            <div style={row} key={i}>
              <input className="cotext" placeholder="탈퇴자" value={s.outName} onChange={(e) => setSwap(i, { outName: e.target.value })} style={{ maxWidth: 104 }} />
              <input className="cotext" placeholder="좌" value={s.outShare} onChange={(e) => setSwap(i, { outShare: e.target.value })} style={{ maxWidth: 52 }} />
              <span style={{ color: '#64748B', fontWeight: 700 }}>→</span>
              <input className="cotext" placeholder="신규자" value={s.inName} onChange={(e) => setSwap(i, { inName: e.target.value })} style={{ maxWidth: 104 }} />
              <input className="cotext" placeholder="좌" value={s.inShare} onChange={(e) => setSwap(i, { inShare: e.target.value })} style={{ maxWidth: 52 }} />
              <button className="rbtn" onClick={() => delSwap(i)} title="행 삭제">✕</button>
            </div>
          ))}
          <div style={row}>
            <button className="rbtn" onClick={addSwap}>+ 행 추가</button>
            <span className="grow" />
            <button className="btn pri sm" disabled={!!busy} onClick={gen}>{busy === 'gen' ? '생성 중…' : real ? '♻ 변경내역 재생성' : '📄 변경내역 생성'}</button>
          </div>
        </div>
      ) : null}

      {/* PDF 오버레이 전용: Word(docx) 줄 없음 */}
      <div className="s3file pdf">
        <span className="rf">📕 {changeFileName(p, 'pdf')}</span>
        {cur.kind === 'seal' ? <span className="pill p-blue">✍ 서명 완료파일</span> : real ? <span className="pill p-gray">PDF 변환됨</span> : <span className="pill p-gray">미생성</span>}
        <span className="grow" />
        <button className="rbtn" disabled={!!busy || !cur.s3pdf} onClick={pv}>👁 미리보기</button>
        <button className="rbtn" disabled={!!busy || !cur.s3pdf} onClick={() => dl('pdf')}>⬇ 원본</button>
        <button className="rbtn" disabled={!!busy} onClick={openSign} title="고객이 직접 서명/날인 → 날인본으로 저장">✍ 서명</button>
        {more && <UpLabel text={busy === 'up' ? '업로드 중…' : '날인본 업로드'} disabled={!!busy} onUp={(f) => replace(f, 'seal')} />}
        <button className="rbtn" onClick={() => setMore((m) => !m)} title="날인본 업로드">{more ? '✕ 닫기' : '⋯ 기타'}</button>
      </div>
      {editUrl ? <PdfEditor fileUrl={editUrl} onSaveBlob={async (blob: Blob) => { await replace(new File([blob], changeFileName(p, 'pdf'), { type: 'application/pdf' }), 'seal'); }} onSaved={closeEditor} onClose={closeEditor} /> : null}
    </div>
  );
}
